import React from 'react'

import { useRouter } from 'next/router'

import Divider from 'components/Divider'
import { useAppContext } from 'context/AppContext'
import { useEventContext } from 'context/EventContext'
import { usePreload } from 'context/PreloadContext'
import detachTask from 'helpers/detachTask'
import { FACETS_EVENT_NAMES } from 'helpers/filters'
import useSecureFetch from 'hooks/useSecureFetch'

import Facet from './components/Facet'

/** @type {import("./types").default} */
const Facets = ({ facets, disableSPA = false, softSPA = false, softUpdate }) => {
  const router = useRouter()
  const { status } = usePreload()
  const secureFetch = useSecureFetch()
  const { sendEvent } = useEventContext()
  const { category = {} } = useAppContext()

  const handleExpandClick =
    ({ facetKey }) =>
    (event, { isExpanded }) => {
      sendEvent({
        engagementComponent: 'filter',
        engagementDestination: isExpanded ? 'filter_expand-card' : 'filter_collapse-card',
        engagementDetails: `filter-expansion-panel_${FACETS_EVENT_NAMES[facetKey]}`,
        name: 'engagement_filter',
      })
    }

  const sendInteractionTracker = ({ facet, value }) => {
    if (!category?.id) {
      return
    }

    const postData = {
      category_id: category.id,
      facet,
      value,
    }

    secureFetch('/api/filters/interaction/', {
      keepalive: true,
      method: 'POST',
      headers: {
        type: 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(postData),
    })
  }

  const handleItemClick = (event, { facet, href, value, label, isChecked }) => {
    const isRatingSelection = facet === 'rating_stars_ge'
    const isSelecting = isChecked || isRatingSelection

    const filterValue = label && !isNaN(parseInt(value)) ? label.replaceAll(' ', '-').toLowerCase() : value
    const engagementDestination = isSelecting
      ? `add_filter_${FACETS_EVENT_NAMES[facet]}-options`
      : `remove_filter_${FACETS_EVENT_NAMES[facet]}-options`
    const engagementDetails = isSelecting ? `add_${filterValue}` : `remove_${filterValue}`

    sendEvent({
      engagementComponent: 'filter',
      engagementDestination,
      engagementDetails,
      name: 'engagement_filter',
    })

    if (isSelecting) {
      sendInteractionTracker({ facet, value })
    }

    detachTask(() => {
      if (softSPA) {
        softUpdate(href)
      } else if (disableSPA) {
        window.location.href = href
      } else {
        router.push(href, undefined, { scroll: false })
      }
    })
  }

  const componentFacets = {
    category_leaders_facet: Facet.CategoryLeaders,
    rating_stars_ge: Facet.RatingStars,
  }

  return (
    <>
      {Object.keys(facets).map(facetKey => {
        const Component = componentFacets[facetKey] || Facet.Default
        const facet = facets[facetKey]
        return (
          facet && (
            <React.Fragment key={facetKey}>
              <Component
                status={status}
                facet={facet}
                facetKey={facetKey}
                onItemClick={handleItemClick}
                onExpandClick={handleExpandClick}
                category={category}
              />
              <Divider />
            </React.Fragment>
          )
        )
      })}
    </>
  )
}

export default Facets
